import {
  renderDate,
  renderDateTime,
  renderOrderStatus,
  renderTextLineClamp,
  renderPrice,
} from 'helpers';
import { TFunc, useTranslation } from 'libs/i18n';
import { TableColumn } from 'libs/ui';

import { OrdersListItem } from '../../types';

import { renderClient } from './renders/client';
import { renderEmployee } from './renders/employee';

interface GetColumnsArgs {
  brokerTimezoneOffset: number;
}

export const getColumns = (
  t: TFunc,
  { brokerTimezoneOffset }: GetColumnsArgs
): TableColumn<OrdersListItem>[] => [
  {
    field: 'orderType',
    title: t('order.orders.columns.orderType'),
    flex: 1,
    minWidth: 138,
    renderCell: (item) =>
      renderTextLineClamp(t(`order.orderTypes.${item.orderType}`)),
  },
  {
    field: 'number',
    title: t('order.orders.columns.number'),
    flex: 1,
    minWidth: 144,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'signedAt',
    title: t('order.orders.columns.signedAt'),
    flex: 1,
    minWidth: 109,
    sortable: true,
    renderCell: (item, field) =>
      renderDateTime(item, field, brokerTimezoneOffset),
  },
  {
    field: 'client',
    title: t('order.orders.columns.clientName'),
    flex: 1,
    minWidth: 225,
    renderCell: renderClient,
  },
  {
    field: 'operationType',
    title: t('order.orders.columns.operationType'),
    flex: 1,
    minWidth: 165,
    renderCell: (item) =>
      renderTextLineClamp(t(`order.operationTypes.${item.operationType}`)),
  },

  {
    field: 'startDate',
    title: t('order.orders.columns.startDate'),
    flex: 1,
    minWidth: 109,
    sortable: true,
    renderCell: (item, field) => renderDate(item, field, brokerTimezoneOffset),
  },
  {
    field: 'endDate',
    title: t('order.orders.columns.endDate'),
    flex: 1,
    minWidth: 109,
    sortable: true,
    renderCell: (item, field) => renderDate(item, field, brokerTimezoneOffset),
  },
  {
    field: 'status',
    title: t('order.orders.columns.status'),
    flex: 1,
    minWidth: 296,
    sortable: true,
    renderCell: (item) => renderOrderStatus(item),
  },
  {
    field: 'closedDate',
    title: t('order.orders.columns.closedDate'),
    flex: 1,
    minWidth: 109,
    sortable: true,
    renderCell: (item, field) => renderDate(item, field, brokerTimezoneOffset),
  },
  {
    field: 'isin',
    title: t('order.orders.columns.isin'),
    flex: 1,
    minWidth: 160,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'currency',
    title: t('order.orders.columns.currency'),
    flex: 1,
    minWidth: 75,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'unitPrice',
    title: t('order.orders.columns.unitPrice'),
    flex: 1,
    minWidth: 100,
    renderCell: renderPrice,
  },
  {
    field: 'quantity',
    title: t('order.orders.columns.quantity'),
    flex: 1,
    minWidth: 102,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'transactionAmount',
    title: t('order.orders.columns.transactionAmount'),
    flex: 1,
    minWidth: 69,
    renderCell: renderPrice,
  },
  {
    field: 'fmNumber',
    title: t('order.orders.columns.fmNumber'),
    flex: 1,
    minWidth: 120,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'employee',
    title: t('order.orders.columns.employee'),
    flex: 1,
    minWidth: 223,
    renderCell: renderEmployee,
  },
];

export const useColumns = ({ brokerTimezoneOffset }: GetColumnsArgs) => {
  const { t } = useTranslation();
  const columns = getColumns(t, { brokerTimezoneOffset });

  return columns;
};
